import type { ContactInteraction, OutreachLogEntry } from '../../services/api'
import { interactionDate } from '../../constants/networking'

// A row is an interaction, an outreach with no interaction of its own, or an
// interaction carrying the follow-up state of the outreach that created it.
export interface ContactActivityRow {
  id: string
  // When it happened: the interaction's date, or the send time for an
  // outreach row that has no interaction.
  at: string
  interaction?: ContactInteraction
  followUp?: OutreachLogEntry
}

/**
 * Interactions and outreach follow-ups as one list, newest first.
 *
 * log-outreach writes an interaction for every send and stamps it with
 * outreachLogId, so a send is one row here, not two. Outreach logged before
 * that link existed has no interaction pointing at it; those entries keep a
 * row of their own so a pending follow-up is never dropped.
 *
 * Either list may be loaded without the other (the outreach log is not fetched
 * for read-only viewers, and a read can lag a write), so each row stands alone
 * when its counterpart is absent.
 */
export function buildContactActivity(
  interactions: ContactInteraction[],
  outreachEntries: OutreachLogEntry[]
): ContactActivityRow[] {
  const entriesById = new Map(outreachEntries.map((e) => [e.id, e]))
  const claimed = new Set<string>()

  const rows: ContactActivityRow[] = interactions.map((interaction) => {
    const followUp = interaction.outreachLogId ? entriesById.get(interaction.outreachLogId) : undefined
    if (followUp) claimed.add(followUp.id)
    return { id: interaction.id, at: interactionDate(interaction), interaction, followUp }
  })

  for (const entry of outreachEntries) {
    if (claimed.has(entry.id)) continue
    rows.push({ id: `outreach-${entry.id}`, at: entry.sentAt, followUp: entry })
  }

  const time = (at: string) => {
    const ms = Date.parse(at)
    return Number.isNaN(ms) ? -Infinity : ms
  }

  // Array.prototype.sort is stable, so equal times keep interactions ahead of
  // unmatched outreach, in the order they were given.
  return rows.sort((a, b) => {
    const diff = time(b.at) - time(a.at)
    return Number.isNaN(diff) ? 0 : diff
  })
}

export type FollowUpState = 'overdue' | 'due' | 'completed' | 'dismissed'

export function followUpState(
  entry: Pick<OutreachLogEntry, 'followUpStatus' | 'followUpDueAt'>,
  now: number
): FollowUpState {
  if (entry.followUpStatus === 'completed') return 'completed'
  if (entry.followUpStatus === 'dismissed') return 'dismissed'
  return Date.parse(entry.followUpDueAt) <= now ? 'overdue' : 'due'
}
